import { shipmentNumber } from '@locales/id/labels';
import type { Cylinder, Permission, Scope } from '@lib/api';

/** A missing cylinder that a reader saw after the receipt can be marked found. */
export const seenSinceReceipt = (lastSeenAt: string | null | undefined, receivedAt: string | null | undefined) => !!lastSeenAt && !!receivedAt && lastSeenAt > receivedAt;

/** Same rule the backend applies when loading (shown on the operator's lookup screen). */
export const canBeLoaded = (c: Cylinder) => c.status === 'ACTIVE' && !c.testOverdue && c.positionStatus === 'AT_LOCATION' && !c.currentShipmentId;

/** Where it is, next to the position chip: the location, or the Surat Jalan it travels on. */
export const positionPlace = (c: Pick<Cylinder, 'positionStatus' | 'locationName' | 'currentShipmentId' | 'currentShipmentNumber'>) =>
  c.positionStatus === 'AT_LOCATION' ? c.locationName : c.currentShipmentId ? shipmentNumber(c.currentShipmentNumber) : '';

/**
 * Why the signed-in person may not edit this cylinder, or null when they may (the backend decides in the end, 403
 * OUT_OF_SCOPE, 409 CYLINDER_DESTROYED): a destroyed cylinder never changes again ('destroyed', the action is hidden
 * and a banner says why); without `cylinder.update` the action is hidden ('role'); a national or province
 * account edits any it can see; a location account (operator) only one recorded at its own location (in transit or
 * missing is for admins).
 */
export function editBlock(c: Pick<Cylinder, 'status' | 'positionStatus' | 'currentLocationId'>, scope: Scope, can: (p: Permission) => boolean): 'destroyed' | 'role' | 'away' | 'elsewhere' | null {
  if (c.status === 'DESTROYED') return 'destroyed';
  if (!can('cylinder.update')) return 'role';
  if (scope.kind !== 'location') return null;
  if (c.positionStatus !== 'AT_LOCATION') return 'away';
  return c.currentLocationId === scope.locationId ? null : 'elsewhere';
}

/**
 * Why the tag cannot be changed now, by the server's rule (at a Titik Distribusi, not on a Surat Jalan being loaded),
 * or null when it can. The server decides in the end (409 CYLINDER_IN_TRANSIT / _MISSING / _ON_SHIPMENT).
 */
export function retagBlock(c: Pick<Cylinder, 'positionStatus' | 'currentShipmentId' | 'currentLocationId'>): 'IN_TRANSIT' | 'MISSING' | 'ON_SHIPMENT' | null {
  if (c.positionStatus === 'IN_TRANSIT') return 'IN_TRANSIT';
  if (c.positionStatus === 'MISSING' || c.currentLocationId === null) return 'MISSING';
  return c.currentShipmentId !== null ? 'ON_SHIPMENT' : null;
}
